import React from 'react';
import { getSpecificEnv } from '@availity/env-var';
import { avUserApi } from '@availity/api-axios';
import { Box, Button, Card, CardActions, CardContent, Grid, Typography } from '@availity/element';
import { useEffectAsync } from '@availity/hooks';

import { AvailityHeaderLogo, Footer } from '@/components';
import { useAppContext } from '@/context';
import { UserImg, ExistingOrgImg, NewOrganizationImg, OtherAdminImg } from '@/static';

const { host = '' } = window.location;
const lowercaseHost = host?.toLowerCase();
// check if the page is loaded with '.net' and set the proper domain suffix
const isDotNet = lowercaseHost.indexOf('.net') >= 0;
const domainSuffix = isDotNet ? '.availity.net' : '.availity.com';

const currentEnv = getSpecificEnv();
const basePortalUrl =
  currentEnv?.toLowerCase() === 'prod' ? `https://apps${domainSuffix}` : `https://${currentEnv}-apps${domainSuffix}`;

const handleMMONavigation = () => {
  window.top.location.href = `${basePortalUrl}/public/apps/home/#!/loadApp?appUrl=%2Fweb%2Fonboarding%2Forganization-management-ui%2F%23%2F`;
};

const InitialView = () => {
  const { setCurrentNextStepsPage, addMessage, userIdString, setUserIdString } = useAppContext();

  useEffectAsync(async () => {
    if (!userIdString) {
      try {
        const organizationsResponse = await avUserApi.me();
        const { userId = '' } = organizationsResponse;
        setUserIdString(userId);
      } catch (error) {
        if (error) {
          addMessage(
            "Oops! Something went wrong when fetching the logged in user's data. Reload the page and try again."
          );
        }
      }
    }
  }, []);

  return (
    <Grid
      id="next-steps-start-page-grid-container"
      container
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      spacing={0}
      sx={{ minHeight: '100vh', justifyContent: 'center' }}
    >
      <Grid
        size={{ xs: 12 }}
        display="flex"
        flexDirection="column"
        alignItems="center"
        flexGrow={1}
        sx={{ minHeight: '100vh', width: '100%' }}
      >
        <AvailityHeaderLogo />
        <Grid direction="column" alignItems="center" justifyContent="center">
          <Box textAlign="center">
            <Typography variant="h1" mb={2}>
              Welcome to Availity Essentials!
            </Typography>
          </Box>
          <Box textAlign="center" mb={2}>
            <img src={UserImg} alt="User Icon" style={{ height: 75 }} />
            <Box>{userIdString}</Box>
          </Box>
          <Typography variant="subtitle1" textAlign="center" mb={2}>
            Before you can use Availity, you must associate yourself to an organization.
          </Typography>
          <Typography variant="subtitle1" textAlign="center" mb={2}>
            <Box component="span" sx={{ fontWeight: 'bold ' }}>
              Which of these sounds most like you?
            </Box>
          </Typography>
          <Grid display="flex" flexDirection="row" flexWrap="wrap" justifyContent="center" sx={{ marginX: 4 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ display: 'flex', flexDirection: 'column', margin: 1, height: '420px' }}>
                <CardContent>
                  <Box textAlign="center" mb={4}>
                    <Typography variant="subtitle1">
                      My co-workers already use Availity, and I need access to our existing organization.
                    </Typography>
                  </Box>
                  <Box mb={4} textAlign="center">
                    <img src={ExistingOrgImg} alt="Existing Organization Icon" style={{ height: 150 }} />
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'end', marginTop: 'auto' }}>
                  <Button color="tertiary" size="small" onClick={() => setCurrentNextStepsPage('existingOrg')}>
                    Continue
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ display: 'flex', flexDirection: 'column', margin: 1, height: '420px' }}>
                <CardContent>
                  <Box textAlign="center" mb={4}>
                    <Typography variant="subtitle1">
                      I need to register a new organization and will be the primary administrator.
                    </Typography>
                  </Box>
                  <Box mb={4} textAlign="center">
                    <img src={NewOrganizationImg} alt="New Organization Icon" style={{ height: 150 }} />
                  </Box>
                  <Box textAlign="center">
                    <Typography variant="subtitle2">
                      <Box component="span" sx={{ fontWeight: 'bold' }}>
                        Note: Personal ID verification is required to register an organization
                      </Box>
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'end', marginTop: 'auto' }}>
                  <Button color="tertiary" size="small" onClick={handleMMONavigation}>
                    Continue
                  </Button>
                </CardActions>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ display: 'flex', flexDirection: 'column', margin: 1, height: '420px' }}>
                <CardContent>
                  <Box textAlign="center" mb={4}>
                    <Typography variant="subtitle1">
                      Our office must register our first organization but someone else should be the primary
                      administrator.
                    </Typography>
                  </Box>
                  <Box mb={4} textAlign="center">
                    <img src={OtherAdminImg} alt="Other Admin Icon" style={{ height: 150 }} />
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'end', marginTop: 'auto' }}>
                  <Button color="tertiary" size="small" onClick={() => setCurrentNextStepsPage('newOrgNeedsAdmin')}>
                    Continue
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          </Grid>
        </Grid>
        <Footer />
      </Grid>
    </Grid>
  );
};

export default InitialView;
